import React, { useState } from 'react';

import { ValidationRuleRunItemLocationDTO } from '@recurrency/core-api-schema/dist/validationRules/getValidationRuleRunsByRuleId';
import { ColumnType } from 'antd/lib/table';

import { PlanningSidePane } from 'pages/purchasing/PlanningPage/PlanningSidePane';

import { AsyncTable } from 'components/AsyncTable';
import { UseAsyncTableProps } from 'components/AsyncTable/useAsyncTableProps';
import { PlanningLink } from 'components/Links';
import { PlanningStatusBadge } from 'components/recipes/PlanningStatusBadge';
import { BadgeStatus } from 'components/recipes/StatusBadge';
import { TableSplitPage } from 'components/recipes/TableSplitPage';
import { DemandPattern, DemandPatternTooltip } from 'components/Tooltip/DemandPatternTooltip';
import { InfoTooltip } from 'components/Tooltip/InfoTooltip';

import { formatDate } from 'utils/formatting';
import { makePath, routePaths } from 'utils/routes';
import { asKeyOf } from 'utils/tables';

export const RuleDetailRunTable = ({
  tableProps,
}: {
  tableProps: UseAsyncTableProps<ValidationRuleRunItemLocationDTO>;
}) => {
  const [focusedRecord, setFocusedRecord] = useState<ValidationRuleRunItemLocationDTO | null>(null);

  const columns: ColumnType<ValidationRuleRunItemLocationDTO>[] = [
    {
      title: 'Item ID',
      render: (_: ValidationRuleRunItemLocationDTO, item: ValidationRuleRunItemLocationDTO) => (
        <div>
          <PlanningLink
            to={makePath(routePaths.demandPlanning.planning, null, {
              query: item.item_id,
              where: {
                location: [item.location],
              },
            })}
          >
            {item.item_id}
          </PlanningLink>
          <div>{item.item_name}</div>
        </div>
      ),
      dataIndex: asKeyOf<ValidationRuleRunItemLocationDTO>('item_id'),
    },
    {
      title: 'Location',
      dataIndex: asKeyOf<ValidationRuleRunItemLocationDTO>('location'),
    },
    {
      title: 'Status',
      dataIndex: asKeyOf<ValidationRuleRunItemLocationDTO>('planning_status'),
      render: (status: BadgeStatus, record: ValidationRuleRunItemLocationDTO) => (
        <PlanningStatusBadge status={status} record={record} />
      ),
    },
    {
      title: (
        <InfoTooltip title="Demand patterns group items based on consistency of orders and variance in demand quantity. Seasonality is automatically factored into all non-Sparse patterns.">
          Pattern
        </InfoTooltip>
      ),
      dataIndex: asKeyOf<ValidationRuleRunItemLocationDTO>('demand_pattern'),
      render: (demandPattern: DemandPattern) => <DemandPatternTooltip demandPattern={demandPattern} />,
    },
    {
      title: 'Last Run Time',
      dataIndex: asKeyOf<ValidationRuleRunItemLocationDTO>('lastRunTime'),
      render: (lastRunTime: string) => formatDate(lastRunTime, true, true),
      sorter: true,
    },
  ];

  return (
    <TableSplitPage
      left={
        <AsyncTable
          stickyHeader
          columns={columns}
          tableProps={tableProps}
          rowClassName={(record: ValidationRuleRunItemLocationDTO) =>
            ['focusable-row', record === focusedRecord ? 'focused-row' : ''].join(' ')
          }
          onRow={(record) => ({
            onClick: () => setFocusedRecord(record),
          })}
        />
      }
      right={<PlanningSidePane record={focusedRecord} searchIndexReload={tableProps.reload} />}
    />
  );
};
